import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch, type ApiError } from '../lib/api.js';
import { useAuth } from '../context/AuthContext.js';

interface AuditEntry {
  id: string;
  mod_user_id: string | null;
  mod_username: string;
  mod_display_name: string | null;
  action: string;
  target_user_id: string | null;
  target_username: string | null;
  target_display_name: string | null;
  target_message_id: string | null;
  source: string | null;
  duration_seconds: number | null;
  reason: string | null;
  created_at: string;
}

interface AuditResponse {
  items: AuditEntry[];
  total: number;
  hasMore: boolean;
  limit: number;
  offset: number;
}

const PAGE_SIZE = 50;
const ACTION_OPTIONS = ['', 'send_message', 'delete_message', 'timeout', 'ban', 'unban'];

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('de-DE');
  } catch {
    return iso;
  }
}

export default function AuditLogPage({ onClose }: { onClose: () => void }) {
  const { isBroadcaster } = useAuth();
  const [items, setItems] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filterAction, setFilterAction] = useState('');
  const [filterModId, setFilterModId] = useState('');
  const [filterTargetId, setFilterTargetId] = useState('');
  const [filterSince, setFilterSince] = useState('');
  const [filterUntil, setFilterUntil] = useState('');

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    p.set('limit', String(PAGE_SIZE));
    p.set('offset', String(offset));
    if (filterAction) p.set('action', filterAction);
    if (filterModId) p.set('modId', filterModId);
    if (filterTargetId) p.set('targetId', filterTargetId);
    if (filterSince) p.set('since', new Date(filterSince).toISOString());
    if (filterUntil) p.set('until', new Date(filterUntil).toISOString());
    return p.toString();
  }, [offset, filterAction, filterModId, filterTargetId, filterSince, filterUntil]);

  const load = useCallback(
    async (append: boolean) => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiFetch<AuditResponse>(`/api/audit-log?${queryString}`);
        setItems((prev) => (append ? [...prev, ...data.items] : data.items));
        setTotal(data.total);
        setHasMore(data.hasMore);
      } catch (err) {
        const apiErr = err as ApiError;
        setError(apiErr?.status === 403 ? 'Nur Broadcaster können das Audit-Log einsehen.' : 'Laden fehlgeschlagen.');
      } finally {
        setLoading(false);
      }
    },
    [queryString],
  );

  useEffect(() => {
    if (!isBroadcaster) return;
    void load(offset > 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString, isBroadcaster]);

  const onApplyFilters = useCallback(() => {
    setOffset(0);
  }, []);

  const onResetFilters = useCallback(() => {
    setFilterAction('');
    setFilterModId('');
    setFilterTargetId('');
    setFilterSince('');
    setFilterUntil('');
    setOffset(0);
  }, []);

  if (!isBroadcaster) {
    return (
      <div className="p-6 text-slate-300">
        <p>Diese Seite ist nur für den Broadcaster sichtbar.</p>
        <button onClick={onClose} className="mt-3 text-sm text-slate-400 underline">
          Zurück
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="flex items-center gap-3 px-4 py-3 border-b border-slate-800">
        <button onClick={onClose} className="text-sm text-slate-400 hover:text-slate-200">
          ← Zurück
        </button>
        <h1 className="text-lg font-semibold">Audit-Log</h1>
        <span className="ml-auto text-xs text-slate-400">
          {total.toLocaleString('de-DE')} Einträge
        </span>
      </header>

      <section className="px-4 py-3 border-b border-slate-800 flex flex-wrap gap-2 items-end text-sm">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-400">Aktion</span>
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded px-2 py-1"
          >
            {ACTION_OPTIONS.map((a) => (
              <option key={a} value={a}>
                {a || '— alle —'}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-400">Mod-ID</span>
          <input
            type="text"
            inputMode="numeric"
            value={filterModId}
            onChange={(e) => setFilterModId(e.target.value.replace(/\D/g, ''))}
            className="bg-slate-900 border border-slate-700 rounded px-2 py-1 w-28"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-400">Ziel-ID</span>
          <input
            type="text"
            inputMode="numeric"
            value={filterTargetId}
            onChange={(e) => setFilterTargetId(e.target.value.replace(/\D/g, ''))}
            className="bg-slate-900 border border-slate-700 rounded px-2 py-1 w-28"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-400">Von</span>
          <input
            type="datetime-local"
            value={filterSince}
            onChange={(e) => setFilterSince(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded px-2 py-1"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-400">Bis</span>
          <input
            type="datetime-local"
            value={filterUntil}
            onChange={(e) => setFilterUntil(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded px-2 py-1"
          />
        </label>
        <button
          onClick={onApplyFilters}
          className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600"
        >
          Filtern
        </button>
        <button
          onClick={onResetFilters}
          className="px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700"
        >
          Zurücksetzen
        </button>
      </section>

      <main className="px-4 py-3">
        {error && <p className="text-sm text-red-400 mb-3">{error}</p>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-xs text-slate-400 border-b border-slate-800">
                <th className="py-2 pr-3">Zeit</th>
                <th className="py-2 pr-3">Mod</th>
                <th className="py-2 pr-3">Aktion</th>
                <th className="py-2 pr-3">Ziel</th>
                <th className="py-2 pr-3">Quelle</th>
                <th className="py-2 pr-3">Dauer (s)</th>
                <th className="py-2 pr-3">Grund</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-b border-slate-900 align-top">
                  <td className="py-2 pr-3 whitespace-nowrap text-slate-300">
                    {formatDate(row.created_at)}
                  </td>
                  <td className="py-2 pr-3">
                    {row.mod_display_name || row.mod_username}
                    {row.mod_user_id && (
                      <span className="text-xs text-slate-500 ml-1">#{row.mod_user_id}</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs">{row.action}</td>
                  <td className="py-2 pr-3">
                    {row.target_display_name || row.target_username || '—'}
                    {row.target_user_id && (
                      <span className="text-xs text-slate-500 ml-1">#{row.target_user_id}</span>
                    )}
                  </td>
                  <td className="py-2 pr-3">{row.source || '—'}</td>
                  <td className="py-2 pr-3">{row.duration_seconds ?? '—'}</td>
                  <td className="py-2 pr-3 text-slate-300 max-w-md truncate" title={row.reason ?? ''}>
                    {row.reason || '—'}
                  </td>
                </tr>
              ))}
              {items.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-slate-500 text-sm">
                    Keine Einträge gefunden.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-center mt-4">
          {hasMore && (
            <button
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              disabled={loading}
              className="px-4 py-2 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-sm"
            >
              {loading ? 'Lade…' : 'Mehr laden'}
            </button>
          )}
          {!hasMore && items.length > 0 && (
            <span className="text-xs text-slate-500">— Ende —</span>
          )}
        </div>
      </main>
    </div>
  );
}
